import { Box, DesktopModal, Flex, IconFont, Popover, Text, usePopoverHeight } from '@byecode/ui'
import type { IndependentAction } from '@lighthouse/core'
import type { FlowActionTypes } from '@lighthouse/shared'
import { ListItem4ByecodeUi } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { FlowDetail, type FlowDetailRef } from '@/containers/FlowDetail'
import { FlowDetailShape } from '@/contexts/FlowContext'

import { ActionTypeList } from './ActionTypeList'

const StyledIconWrapper = styled.div<{ active?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: ${p => (p.active ? 'var(--color-main-tint)' : 'var(--color-gray-100)')};
    border-radius: 6px;
    width: 32px;
    height: 32px;
`
interface AddActionProps {
    onAdd: (val: IndependentAction) => void
}

interface CurrentFlowIdAndType {
    id: string
    type: FlowActionTypes
}

export const AddAction: React.FC<AddActionProps> = ({ onAdd }) => {
    const [open, setOpen] = useState(false)
    const [flowModalOpen, setFlowModalOpen] = useState(false)
    const flowDetailRef = React.useRef<FlowDetailRef>(null)
    const [currentFlowIdAndType, setCurrentFlowIdAndType] = useImmer<CurrentFlowIdAndType>({
        id: '',
        type: 'flow'
    })

    const { height: maxHeight, ref } = usePopoverHeight({ opened: open })

    const { id, type } = currentFlowIdAndType

    const handleClose = useCallback(() => {
        setOpen(false)
    }, [])

    const handleAfterCreateFlow = useCallback(
        (id: string, type: FlowActionTypes) => {
            setCurrentFlowIdAndType({
                id,
                type
            })
            setFlowModalOpen(true)
        },
        [setCurrentFlowIdAndType]
    )

    const assembleActionParams = useCallback((value: string, type: FlowActionTypes): IndependentAction => {
        const id = nanoid()
        return {
            id,
            type,
            data: {
                [type]: {
                    flowId: value
                }
            }
        } as IndependentAction
    }, [])

    const handleFlowClose = useCallback(() => {
        setFlowModalOpen(false)
        onAdd(assembleActionParams(id, type))
    }, [assembleActionParams, id, onAdd, type])

    return (
        <Box ref={ref}>
            <Popover withinPortal width={240} position="bottom-start" opened={open} onChange={setOpen}>
                <Popover.Target>
                    <ListItem4ByecodeUi
                        styles={{
                            root: {
                                padding: '8px!important',
                                '&:hover': {
                                    [`${StyledIconWrapper}`]: {
                                        background: 'var(--color-white)'
                                    }
                                }
                            }
                        }}
                        justifyContent="space-between"
                        enableHover
                        alignItems="center"
                    >
                        <Flex gap="8px" alignItems="center">
                            <StyledIconWrapper style={{ width: 32, height: 32 }}>
                                <IconFont type="Add" size={20} fill="var(--color-gray-500)" />
                            </StyledIconWrapper>
                            <Text size={14} color="var(--color-gray-500)">
                                添加动作
                            </Text>
                        </Flex>
                    </ListItem4ByecodeUi>
                </Popover.Target>
                <Popover.Dropdown style={{ maxHeight, overflow: 'auto' }}>
                    <ActionTypeList onChange={onAdd} onClose={handleClose} onAfterCreateFlow={handleAfterCreateFlow} />
                </Popover.Dropdown>
            </Popover>
            <DesktopModal
                open={flowModalOpen}
                withCloseIcon={false}
                styles={{
                    modal: {
                        width: '95vw',
                        height: '90vh'
                    },
                    body: {
                        padding: 0
                    }
                }}
                onClose={() => {
                    if (flowDetailRef.current) {
                        flowDetailRef.current.requestClose()
                        return
                    }

                    setFlowModalOpen(false)
                }}
            >
                <FlowDetail
                    ref={flowDetailRef}
                    id={id}
                    shape={FlowDetailShape.MODAL}
                    onDismiss={() => setFlowModalOpen(false)}
                    onClose={handleFlowClose}
                />
            </DesktopModal>
        </Box>
    )
}
